import { isBrowserSupportSpeaker } from '@/utils/common';
import logger from '@/modules/Logger';

export default {
  namespaced: true,
  state: {
    cameras: [],
    mics: [],
    speakers: [],
    selectedMic: null,
    selectedCamera: null,
    selectedSpeaker: null,
    micSelection: true,
    videoSelection: true,
    speakerSelection: true,
    cameraEnabled: true,
    micEnabled: true,

    cameraDeviceError: null,
    micDeviceError: null
  },
  mutations: {
    /**
     * Set camera state
     *
     * @param {*} state
     * @param {*} payload
     */
    setCameraEnabled(state, payload) {
      state.cameraEnabled = payload;
    },
    /**
     * Set microphone state
     *
     * @param {*} state
     * @param {*} payload
     */
    setMicEnabled(state, payload) {
      state.micEnabled = payload;
    },
    /**
     * Select Mic
     * @param {object} state
     * @param {integer} payload
     */
    selectMic(state, payload) {
      state.selectedMic = payload
    },
    /**
     *
     * @param {object} state
     * @param {boolean} payload
     */
    selectCamera(state, payload) {
      state.selectedCamera = payload
    },
    selectSpeaker(state, payload) {
      state.selectedSpeaker = payload
    },
    /**
     * Switch Video Selection
     * @param {object} state
     * @param {boolean} payload
     */
    switchVideoSelection(state, payload) {
      if (state.cameras.length === 0) {
        state.videoSelection = false;
        return;
      }
      state.videoSelection = payload
    },
    /**
     * Switch Mic Selection
     *
     * @param {object} state
     * @param {boolean} payload
     */
    switchMicSelection(state, payload) {
      if (state.mics.length === 0) {
        state.micSelection = false;
        return;
      }
      state.micSelection = payload
    },
    /**
     * Device Selector
     *
     * @param {object} state
     * @param {string} input
     */
    async setConnectedDevices(state, payload) {
      let selectedDevice = [];
      let listedDevice = [];
      let defaultDeviceGroupId = null

      payload.list.forEach(function (device) {
        if (device.deviceId !== null) {
          const { deviceId, kind, groupId } = device;
          let { label } = device;
          const uniqueDevice = deviceId !== 'default' && deviceId !== 'communications';
          // for chrome
          if (deviceId === 'default') {
            defaultDeviceGroupId = groupId
          }

          if (!deviceId || !uniqueDevice || listedDevice[deviceId]) return;


          listedDevice[deviceId] = true;
          if (label.includes('Default -')) {
            label = label.replace('Default - ', '')
          }
          const isDefault = label.includes('Default') || groupId === defaultDeviceGroupId;
          selectedDevice.push({ deviceId, label, kind, groupId, isDefault });
        }
      });
      switch (payload.kind) {
        case 'audioinput':
          state.micSelection = selectedDevice.length !== 0;
          state.mics = selectedDevice;
          state.micEnabled = !state.micSelection ? false : state.micEnabled;
          break;
        case 'videoinput':
          state.videoSelection = selectedDevice.length !== 0;
          state.cameras = selectedDevice;
          state.cameraEnabled = !state.videoSelection ? false : state.cameraEnabled;
          break;
        case 'audiooutput':
          state.speakerSelection = selectedDevice.length !== 0;
          state.speakers = selectedDevice;
          break;
      }
    },
    setCameraDeviceError(state, data) {
      state.cameraDeviceError = data;
    },
    setMicDeviceError(state, data) {
      state.micDeviceError = data;
    }
  },
  actions: {
    async getMics({ commit }) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = {
        kind: 'audioinput',
        list: devices.filter(device => device.kind === 'audioinput')
      }
      commit('setConnectedDevices', mics);
    },
    async getCameras({ commit }) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = {
        kind: 'videoinput',
        list: devices.filter(device => device.kind === 'videoinput')
      }
      commit('setConnectedDevices', cameras);
    },
    async getSpeakers({ commit, state }) {
      if (!isBrowserSupportSpeaker()) {
        return;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();

      const speakers = {
        kind: 'audiooutput',
        list: devices.filter(device => device.kind === 'audiooutput')
      }

      const filteredSpeakers = speakers.list?.filter(item => item.deviceId !== 'default' && item.deviceId !== 'communications');
      const isSpeakerListChanged = state.speakers.length ? state.speakers.length !== filteredSpeakers?.length : false;
      const isSelectedUnavailable = speakers.list.filter(speaker => speaker.label.includes(state.selectedSpeaker)).length === 0;

      if (isSpeakerListChanged || isSelectedUnavailable) {
        logger.info('Setting speaker to default', { operation: 'device:speaker', isSpeakerListChanged, isSelectedUnavailable });
        for(let i = 0; i < speakers.list.length; i++) {
          let label = speakers.list[i].label
          let defaultDeviceId = speakers.list[i].deviceId === 'default';
          if (label.includes('Default - ') || defaultDeviceId) {
            commit('selectSpeaker', label.replace('Default - ', ''))
            break;
          }
        }
      }

      commit('setConnectedDevices', speakers);
    },
    selectCamera({ commit }, idx = 0) {
      commit('selectCamera', idx);
    },
    selectMic({ commit }, idx = 0) {
      commit('selectMic', idx);
    },
    selectSpeaker({ commit }, idx = 0) {
      commit('selectSpeaker', idx);
    },
    setMediaDeviceError({ commit }, errorData) {
      const errorCommit = errorData?.device === 'audio' ? 'setMicDeviceError' : 'setCameraDeviceError';
      commit(errorCommit, errorData?.name ?? null);
    },
    async selectExternalMic({ commit, state }) {
      const mics = state.mics;
      const lastMic = mics.at(-1);
      if (lastMic?.label.includes('iPhone') && mics.length > 1) {
        const firstMic = mics[0];
        commit('selectMic', firstMic.label);
        return firstMic.deviceId;
      }
      commit('selectMic', lastMic?.label.replace('Default - ', ''));
      return lastMic?.deviceId;
    }
  },
  getters: {
    /**
     * get Camera
     *
     * @return string
     */
    camera: state => {
      return state.cameras.find(key => key.label === state.selectedCamera)
    },
    /**
     * get Mic
     *
     * @return string
     */
    mic: state => {
      return state.mics.find(key => key.label === state.selectedMic)
    },
    /**
     * get Speaker
     *
     * @return string
     */
    speaker: state => {
      return state.speakers.find(key => key.label === state.selectedSpeaker)
    },
    /**
     * Check if camera error is permission denied
     *
     * @return boolean
     */
    isCameraPermissionDenied: state => {
      return state.cameraDeviceError === 'NotAllowedError';
    },

    /**
     * Check if microphone error is permission denied
     *
     * @return boolean
     */
    isMicPermissionDenied: state => {
      return state.micDeviceError === 'NotAllowedError';
    },

    /**
     * Check if camera error is not found
     *
     * @return boolean
     */
    isCameraNotFound: state => {
      return state.cameraDeviceError === 'NotFoundError';
    },

    /**
     * Check if mic error is not found
     *
     * @return boolean
     */
    isMicNotFound: state => {
      return state.micDeviceError === 'NotFoundError';
    },
    
    /**
     * Check if mic has an error
     * 
     * @return boolean
     */
    hasMicError: state =>  {
      return !!state.micDeviceError;
    },

    /**
     * Check if camera has an error
     * 
     * @return boolean
     */
    hasCameraError: state =>  {
      return !!state.cameraDeviceError;
    },

    cameraSelectPlaceHolder: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.isLessonEnded && (getters.hasCameraError || state.cameras.length == 0)) { 
        let errorType = getters.isCameraPermissionDenied ? 'permissionDenied' : 'notFound';
        return `deviceErrorGuide.title.${errorType}`;
      } else {
        return 'deviceCheck.selectCamera';
      }
    },

    micSelectPlaceHolder: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.isLessonEnded && (getters.hasMicError || state.mics.length == 0)) { 
        let errorType = getters.isMicPermissionDenied ? 'permissionDenied' : 'notFound';
        return `deviceErrorGuide.title.${errorType}`;
      } else {
        return 'deviceCheck.selectMicrophone';
      }
    },

    speakerSelectPlaceHolder: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.isLessonEnded && getters.isMicPermissionDenied) { 
        return `systemDefault`;
      } else {
        return 'deviceCheck.selectSpeaker';
      }
    }
  }
};
